import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { CalendarDatePicker } from '@/components/CalendarDatePicker';
import { ChevronLeftIcon, ClientsIcon, SparkIcon } from '@/components/Icons';
import { StatMetric } from '@/components/StatMetric';
import { useSessionState } from '@/context/SessionContext';
import { useVisitorState } from '@/context/VisitorContext';
import { fetchClient, fetchClients } from '@/lib/api';
import {
  buildCustomerSummaries,
  formatCustomerVisitDate,
  formatCustomerVisitTime,
  formatCustomerStatusLabel,
  mapClientDetailToSummary,
  normalizeCustomerKey,
  type CustomerStatus,
  type CustomerSummary,
} from '@/lib/customers';
import { formatCompactNumber, formatInrCurrency, getInitials } from '@/lib/format';
import type { ClientRecord } from '@/lib/types';

const STATUS_FILTERS: Array<{ key: 'all' | CustomerStatus; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'follow_up', label: 'Follow up' },
  { key: 'new', label: 'New' },
  { key: 'not_answering', label: 'Not answering' },
];

const STATUS_TONE_CLASS: Record<CustomerStatus, string> = {
  follow_up: 'status-follow-up',
  new: 'status-new',
  not_answering: 'status-not-answering',
  irrelevant: 'status-irrelevant',
  junk: 'status-junk',
};

type ClientDetailTab = 'latest' | 'history' | 'reviews';

const CLIENT_DETAIL_TABS: Array<{ key: ClientDetailTab; label: string }> = [
  { key: 'latest', label: 'Latest visit' },
  { key: 'history', label: 'Visit history' },
  { key: 'reviews', label: 'Reviews' },
];

const CUSTOMER_ACTIONS: CustomerStatus[] = ['follow_up', 'not_answering', 'junk', 'irrelevant'];

function CustomerCard({
  customer,
  onPress,
}: {
  customer: CustomerSummary;
  onPress: () => void;
}) {
  return (
    <button className="list-item interactive" type="button" onClick={onPress}>
      <div className="media-row start media-copy">
        <div className="avatar">
          {getInitials(customer.name)}
        </div>

        <div>
          <div className="media-row start spread-row">
            <div>
              <strong>{customer.name}</strong>
              <span>{customer.phone}</span>
            </div>
            <span className={`badge soft status-chip ${STATUS_TONE_CLASS[customer.status]}`}>
              {customer.statusLabel}
            </span>
          </div>

          <p>
            {customer.totalVisits} visit{customer.totalVisits === 1 ? '' : 's'} · {formatInrCurrency(customer.totalSpent)}
          </p>
          <p>{customer.latestServices.join(' · ')}</p>
          <p>Last handled by {customer.latestVisit.staffName}</p>
        </div>
      </div>
    </button>
  );
}

function ClientDetail({
  customer,
  onBack,
  onStatusChange,
}: {
  customer: CustomerSummary;
  onBack: () => void;
  onStatusChange: (status: CustomerStatus) => void;
}) {
  const [activeTab, setActiveTab] = useState<ClientDetailTab>('latest');
  const tabButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const latestVisit = customer.visits[0];
  const earlierVisits = customer.visits.slice(1);
  const availableTabs = CLIENT_DETAIL_TABS.filter((tab) => {
    if (tab.key === 'history') {
      return earlierVisits.length > 0;
    }

    if (tab.key === 'reviews') {
      return customer.reviews.length > 0;
    }

    return true;
  });

  useEffect(() => {
    setActiveTab('latest');
  }, [customer.key]);

  function focusTab(index: number) {
    const nextTab = availableTabs[index];

    if (!nextTab) {
      return;
    }

    setActiveTab(nextTab.key);
    tabButtonRefs.current[index]?.focus();
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      focusTab((index + 1) % availableTabs.length);
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      focusTab((index - 1 + availableTabs.length) % availableTabs.length);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      focusTab(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      focusTab(availableTabs.length - 1);
    }
  }

  return (
    <div className="client-detail-layout">
      <div className="panel client-summary-panel">
        <div className="panel-title-row">
          <div>
            <button className="ghost-button" type="button" onClick={onBack}>
              <ChevronLeftIcon size={16} />
              Back to clients
            </button>
          </div>
          <div className="badge">
            <ClientsIcon size={14} />
            {customer.totalVisits} visits
          </div>
        </div>

        <div className="list-item centered client-summary-card">
          <div className="media-row lg">
            <div className="avatar">{getInitials(customer.name)}</div>
            <div>
              <strong>{customer.name}</strong>
              <span>{customer.phone}</span>
              <p>First visit {formatCustomerVisitDate(customer.firstVisitAt)}</p>
              <p>Latest visit {formatCustomerVisitDate(customer.latestVisitAt)}</p>
            </div>
          </div>
          <div className="item-meta">
            <span className={`badge soft bottom-stack-sm status-chip ${STATUS_TONE_CLASS[customer.status]}`}>
              {customer.statusLabel}
            </span>
            <strong>{formatInrCurrency(customer.totalSpent)}</strong>
          </div>
        </div>
      </div>

      <div className="panel client-workspace-panel">
        <div className="detail-toolbar">
          <div className="detail-tabs" role="tablist" aria-label="Client detail sections">
            {availableTabs.map((tab, index) => {
              const isActive = activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  ref={(element) => {
                    tabButtonRefs.current[index] = element;
                  }}
                  type="button"
                  role="tab"
                  id={`client-detail-tab-${tab.key}`}
                  aria-controls={`client-detail-tabpanel-${tab.key}`}
                  aria-selected={isActive}
                  className={`segmented-button${isActive ? ' active' : ''}`}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveTab(tab.key)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}>
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="detail-meta">
            <span className="badge soft">{customer.totalVisits} visits</span>
            <span className="badge soft">{customer.statusLabel}</span>
          </div>
        </div>

        <div
          className="detail-tab-panel"
          role="tabpanel"
          id={`client-detail-tabpanel-${activeTab}`}
          aria-labelledby={`client-detail-tab-${activeTab}`}>
          {activeTab === 'latest' ? (
            <div className="detail-stack">
              <div className="section-heading detail-section-heading">
                <div>
                  <h2>Latest visit</h2>
                  <p>
                    {formatCustomerVisitDate(latestVisit.createdAt)} · {formatCustomerVisitTime(latestVisit.createdAt)}
                  </p>
                </div>
                <div className="badge soft">{latestVisit.staffName}</div>
              </div>

              <div className="list-item detail-card">
                <div>
                  <strong>{latestVisit.services.join(' · ')}</strong>
                  <span>{latestVisit.feedbackDeliveryStatus === 'failed' ? 'Feedback failed' : 'Feedback sent'}</span>
                  <p>Total {formatInrCurrency(latestVisit.totalAmount)}</p>
                </div>
                <div className="item-meta">
                  <strong>{customer.statusLabel}</strong>
                  <span className="helper">Most recent activity</span>
                </div>
              </div>
            </div>
          ) : null}

          {activeTab === 'history' ? (
            <div className="detail-stack">
              <div className="section-heading detail-section-heading">
                <div>
                  <h2>Visit history</h2>
                  <p>Earlier visits for this customer.</p>
                </div>
                <div className="badge soft">
                  {earlierVisits.length} earlier visit{earlierVisits.length === 1 ? '' : 's'}
                </div>
              </div>

              <div className="list">
                {earlierVisits.map((visit) => (
                  <div className="list-item detail-card" key={visit.id}>
                    <div>
                      <strong>{formatCustomerVisitDate(visit.createdAt)}</strong>
                      <span>
                        {formatCustomerVisitTime(visit.createdAt)} · {visit.staffName}
                      </span>
                      <p>{visit.services.join(' · ')}</p>
                    </div>
                    <div className="item-meta">
                      <span className="badge soft bottom-stack-sm">
                        {visit.feedbackDeliveryStatus === 'failed' ? 'Failed' : 'Sent'}
                      </span>
                      <strong>{formatInrCurrency(visit.totalAmount)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {activeTab === 'reviews' ? (
            <div className="detail-stack">
              <div className="section-heading detail-section-heading">
                <div>
                  <h2>Reviews</h2>
                  <p>Feedback, notes, and visibility state.</p>
                </div>
                <div className="badge soft">
                  {customer.reviews.length} review{customer.reviews.length === 1 ? '' : 's'}
                </div>
              </div>

              <div className="list">
                {customer.reviews.map((review) => (
                  <div className="list-item detail-card" key={review.id}>
                    <div>
                      <strong>{review.rating ? `${review.rating}/5 rating` : 'Customer response'}</strong>
                      <span>
                        {formatCustomerVisitDate(review.createdAt)} · {review.staffName}
                      </span>
                      {review.answers.length > 0 ? (
                        <div className="review-answers">
                          {review.answers.map((item) => (
                            <p key={`${review.id}-${item.question}`} className="review-answer-row">
                              <span className="review-answer-question">{item.question}</span>
                              <span className="review-answer-value">{item.answer}</span>
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p>{review.note ?? 'No note was added for this response.'}</p>
                      )}
                    </div>
                    <div className="item-meta">
                      <span className="badge soft bottom-stack-sm">
                        {review.visibleToClient ? 'Visible' : 'Internal'}
                      </span>
                      <strong>{review.serviceSummary}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="client-actions">
          <div className="section-heading detail-section-heading">
            <div>
              <h2>Customer actions</h2>
              <p>Mirror the mobile follow-up state updates on the web.</p>
            </div>
          </div>

          <div className="chip-row">
            {CUSTOMER_ACTIONS.map((status) => (
              <button key={status} type="button" className={`chip ${STATUS_TONE_CLASS[status]}`} onClick={() => onStatusChange(status)}>
                {formatCustomerStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ClientsPage() {
  const navigate = useNavigate();
  const params = useParams<{ clientKey?: string }>();
  const { sessionRole, sessionStaffId, isAuthenticated } = useSessionState();
  const { isReady, visitors, updateVisitor } = useVisitorState();
  const [selectedStatus, setSelectedStatus] = useState<'all' | CustomerStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [apiClients, setApiClients] = useState<ClientRecord[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiDetailCustomer, setApiDetailCustomer] = useState<CustomerSummary | null>(null);
  const [apiDetailLoading, setApiDetailLoading] = useState(false);
  const [apiDetailError, setApiDetailError] = useState<string | null>(null);

  const requestedKey = typeof params.clientKey === 'string' ? params.clientKey : null;
  const isStaffAccount = sessionRole === 'staff';

  useEffect(() => {
    if (!isAuthenticated) {
      setApiClients([]);
      setApiError(null);
      return;
    }

    const controller = new AbortController();
    let active = true;

    fetchClients(controller.signal)
      .then((payload) => {
        if (!active) {
          return;
        }

        setApiClients(payload.items ?? []);
        setApiError(null);
      })
      .catch((caught) => {
        if (!active || controller.signal.aborted) {
          return;
        }

        setApiError(caught instanceof Error ? caught.message : 'Unable to load clients.');
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !requestedKey) {
      setApiDetailCustomer(null);
      setApiDetailError(null);
      setApiDetailLoading(false);
      return;
    }

    const controller = new AbortController();
    let active = true;

    setApiDetailLoading(true);
    setApiDetailError(null);

    fetchClient(requestedKey, controller.signal)
      .then((detail) => {
        if (!active) {
          return;
        }

        setApiDetailCustomer(mapClientDetailToSummary(detail));
        setApiDetailLoading(false);
      })
      .catch((caught) => {
        if (!active || controller.signal.aborted) {
          return;
        }

        setApiDetailCustomer(null);
        setApiDetailLoading(false);
        setApiDetailError(caught instanceof Error ? caught.message : 'Unable to load client detail.');
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [isAuthenticated, requestedKey]);

  const scopedVisitors = useMemo(
    () => (isStaffAccount && sessionStaffId ? visitors.filter((visitor) => visitor.staffId === sessionStaffId) : visitors),
    [isStaffAccount, sessionStaffId, visitors]
  );

  const dateFilteredVisitors = useMemo(() => {
    const startDate = rangeStart ? new Date(`${rangeStart}T00:00:00`) : null;
    const endDate = rangeEnd ? new Date(`${rangeEnd}T23:59:59.999`) : null;

    if (!startDate && !endDate) {
      return scopedVisitors;
    }

    return scopedVisitors.filter((visitor) => {
      const visitTime = new Date(visitor.createdAt).getTime();

      if (Number.isNaN(visitTime)) {
        return false;
      }

      if (startDate && visitTime < startDate.getTime()) {
        return false;
      }

      if (endDate && visitTime > endDate.getTime()) {
        return false;
      }

      return true;
    });
  }, [rangeEnd, rangeStart, scopedVisitors]);

  const customerSummaries = useMemo(() => buildCustomerSummaries(dateFilteredVisitors), [dateFilteredVisitors]);

  const searchedCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return customerSummaries;
    }

    return customerSummaries.filter((customer) => {
      return (
        customer.name.toLowerCase().includes(query) ||
        customer.phone.toLowerCase().includes(query) ||
        normalizeCustomerKey(customer.phone).includes(normalizeCustomerKey(query))
      );
    });
  }, [customerSummaries, searchQuery]);

  const visibleCustomers = useMemo(() => {
    if (selectedStatus === 'all') {
      return searchedCustomers;
    }

    return searchedCustomers.filter((customer) => customer.status === selectedStatus);
  }, [searchedCustomers, selectedStatus]);

  const selectedCustomer = useMemo(() => {
    if (!requestedKey) {
      return null;
    }

    if (apiDetailCustomer) {
      const localMatch = customerSummaries.find(
        (customer) => customer.key === normalizeCustomerKey(requestedKey)
      );

      if (localMatch) {
        return {
          ...apiDetailCustomer,
          status: localMatch.status,
          statusLabel: localMatch.statusLabel,
          statusTone: localMatch.statusTone,
        } satisfies CustomerSummary;
      }

      return apiDetailCustomer;
    }

    return customerSummaries.find((customer) => customer.key === normalizeCustomerKey(requestedKey)) ?? null;
  }, [apiDetailCustomer, customerSummaries, requestedKey]);

  const totalVisits = visibleCustomers.reduce((sum, customer) => sum + customer.totalVisits, 0);
  const totalSpent = visibleCustomers.reduce((sum, customer) => sum + customer.totalSpent, 0);
  const totalReviews = visibleCustomers.reduce((sum, customer) => sum + customer.reviews.length, 0);
  const usingApiClients = apiClients.length > 0;
  const customerMetricCount = usingApiClients ? apiClients.length : visibleCustomers.length;
  const visitMetricCount = usingApiClients
    ? apiClients.reduce((sum, client) => sum + (client.total_visits ?? client.appointment_count ?? 0), 0)
    : totalVisits;
  const spentMetricValue = usingApiClients
    ? apiClients.reduce((sum, client) => sum + (client.total_spent ?? 0), 0)
    : totalSpent;

  const customerStatusByKey = useMemo(
    () => new Map(customerSummaries.map((customer) => [customer.key, customer.status])),
    [customerSummaries]
  );

  const searchedApiClients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return apiClients;
    }

    return apiClients.filter((client) => {
      return (
        client.name.toLowerCase().includes(query) ||
        client.number.toLowerCase().includes(query) ||
        normalizeCustomerKey(client.number).includes(normalizeCustomerKey(query))
      );
    });
  }, [apiClients, searchQuery]);

  const filteredApiClients = useMemo(() => {
    if (selectedStatus === 'all') {
      return searchedApiClients;
    }

    return searchedApiClients.filter((client) => {
      const key = normalizeCustomerKey(client.key ?? client.number ?? client.id);
      const status = customerStatusByKey.get(key) ?? 'new';

      return status === selectedStatus;
    });
  }, [customerStatusByKey, searchedApiClients, selectedStatus]);

  const statusCounts = useMemo(() => {
    const counts: Record<'all' | CustomerStatus, number> = {
      all: 0,
      follow_up: 0,
      new: 0,
      not_answering: 0,
      irrelevant: 0,
      junk: 0,
    };

    if (usingApiClients) {
      counts.all = searchedApiClients.length;
      searchedApiClients.forEach((client) => {
        const key = normalizeCustomerKey(client.key ?? client.number ?? client.id);
        const status = customerStatusByKey.get(key) ?? 'new';
        counts[status] += 1;
      });
      return counts;
    }

    counts.all = searchedCustomers.length;
    searchedCustomers.forEach((customer) => {
      counts[customer.status] += 1;
    });
    return counts;
  }, [customerStatusByKey, searchedApiClients, searchedCustomers, usingApiClients]);

  const followUps = useMemo(
    () =>
      buildCustomerSummaries(scopedVisitors)
        .filter((customer) => customer.status === 'follow_up')
        .slice(0, 4),
    [scopedVisitors]
  );
  const recentReviews = useMemo(
    () => customerSummaries.flatMap((customer) => customer.reviews).slice(0, 4),
    [customerSummaries]
  );
  const hasVisibleCustomers = usingApiClients
    ? filteredApiClients.length > 0
    : visibleCustomers.length > 0;
  const hasClientMetrics =
    customerMetricCount > 0 || visitMetricCount > 0 || spentMetricValue > 0 || totalReviews > 0;

  async function handleStatusChange(status: CustomerStatus) {
    if (!selectedCustomer) {
      return;
    }

    const result = await updateVisitor(selectedCustomer.latestVisit.id, { customerStatusOverride: status });

    if (!result) {
      setFeedback({ kind: 'error', message: 'Could not update this customer right now.' });
      return;
    }

    setFeedback({ kind: 'success', message: `${selectedCustomer.name} moved to ${status.replace('_', ' ')}.` });
  }

  if (!isReady) {
    return <div className="section">Loading clients...</div>;
  }

  if (requestedKey && !selectedCustomer) {
    if (apiDetailLoading) {
      return <div className="section">Loading client...</div>;
    }

    if (apiDetailError) {
      return (
        <div className="section page-grid">
          <div className="panel">
            <div className="section-heading">
              <div>
                <button className="ghost-button" type="button" onClick={() => navigate('/clients')}>
                  <ChevronLeftIcon size={16} />
                  Back
                </button>
                <h2>Client unavailable</h2>
                <p>{apiDetailError}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (usingApiClients || apiClients.length > 0) {
      const apiMatch = apiClients.find((client) => {
        const key = normalizeCustomerKey(client.key ?? client.number ?? client.id);
        return key === normalizeCustomerKey(requestedKey) || client.id === requestedKey;
      });

      if (apiMatch) {
        const apiVisitCount = apiMatch.total_visits ?? apiMatch.appointment_count ?? 0;
        const apiSpent = apiMatch.total_spent ?? 0;
        const apiRating =
          typeof apiMatch.average_rating === 'number'
            ? apiMatch.average_rating
            : typeof apiMatch.latest_rating === 'number'
              ? apiMatch.latest_rating
              : null;
        const hasApiMetrics =
          apiVisitCount > 0 || apiSpent > 0 || apiRating !== null || Boolean(apiMatch.latest_staff_name);

        return (
          <div className="section page-grid">
            <div className="panel">
              <div className="section-heading">
                <div>
                  <button className="ghost-button" type="button" onClick={() => navigate('/clients')}>
                    <ChevronLeftIcon size={16} />
                    Back
                  </button>
                  <h2>{apiMatch.name}</h2>
                  <p>{apiMatch.number}</p>
                </div>
              </div>
              {hasApiMetrics ? (
                <div className="stats-grid">
                  {apiVisitCount > 0 ? (
                    <StatMetric label="Visits" value={formatCompactNumber(apiVisitCount)} hint="From clients API" />
                  ) : null}
                  {apiSpent > 0 ? (
                    <StatMetric label="Spent" value={formatInrCurrency(apiSpent)} hint="Lifetime spend" />
                  ) : null}
                  {apiRating !== null ? (
                    <StatMetric label="Rating" value={apiRating.toFixed(1)} hint="Average rating" />
                  ) : null}
                  {apiMatch.latest_staff_name ? (
                    <StatMetric label="Staff" value={apiMatch.latest_staff_name} hint="Latest staff" />
                  ) : null}
                </div>
              ) : null}
              {apiMatch.latest_services?.length ? (
                <p className="copy">Latest services: {apiMatch.latest_services.join(' · ')}</p>
              ) : null}
            </div>
          </div>
        );
      }
    }

    return <Navigate to="/clients" replace />;
  }

  if (requestedKey && selectedCustomer) {
    return (
      <div className="section page-grid">
        {feedback ? <div className={`notice ${feedback.kind} bottom-stack-sm`}>{feedback.message}</div> : null}
        <ClientDetail customer={selectedCustomer} onBack={() => navigate('/clients')} onStatusChange={handleStatusChange} />
      </div>
    );
  }

  return (
    <div className="section page-grid">
      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <ClientsIcon size={14} />
            <strong>Clients</strong>
            <span>{usingApiClients ? 'Live API' : 'Local visits'}</span>
          </span>

          <h1 className="display-title">
            <span>Customer relationships</span>
          </h1>

          <div className="button-row hero-actions">
            <button className="primary-button" type="button" onClick={() => navigate('/visitor')}>
              <SparkIcon size={16} />
              New visitor
            </button>
          </div>

          {apiError ? <div className="notice error">{apiError}</div> : null}
        </div>

        {hasClientMetrics ? (
          <div className="stats-grid">
            {customerMetricCount > 0 ? (
              <StatMetric
                label="Customers"
                value={formatCompactNumber(customerMetricCount)}
                hint={usingApiClients ? 'From clients API' : 'Distinct client threads after filters'}
              />
            ) : null}
            {visitMetricCount > 0 ? (
              <StatMetric
                label="Visits"
                value={formatCompactNumber(visitMetricCount)}
                hint={usingApiClients ? 'Total visits from API' : 'All visits in the filtered set'}
              />
            ) : null}
            {spentMetricValue > 0 ? (
              <StatMetric
                label="Spent"
                value={formatInrCurrency(spentMetricValue)}
                hint={usingApiClients ? 'Total spend from API' : 'Revenue attributed to these customers'}
              />
            ) : null}
            {totalReviews > 0 ? (
              <StatMetric
                label="Reviews"
                value={formatCompactNumber(totalReviews)}
                hint="Feedback responses across visits"
              />
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="content-grid">
        <div className="stack">
          <div className="panel">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Search</h2>
                <p className="panel-caption">Name, phone, status, dates</p>
              </div>
            </div>

            <div className="field-grid">
              <label className="field">
                <span className="field-label">Search</span>
                <input
                  className="input"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by name or phone"
                />
              </label>

              <div className="split-grid">
                <label className="field">
                  <span className="field-label">From</span>
                  <CalendarDatePicker
                    ariaLabel="Filter start date"
                    value={rangeStart}
                    onChange={setRangeStart}
                    placeholder="Any start date"
                    maxDate={rangeEnd || undefined}
                    align="start"
                  />
                </label>
                <label className="field">
                  <span className="field-label">To</span>
                  <CalendarDatePicker
                    ariaLabel="Filter end date"
                    value={rangeEnd}
                    onChange={setRangeEnd}
                    placeholder="Any end date"
                    minDate={rangeStart || undefined}
                    align="end"
                  />
                </label>
              </div>

              <div className="client-status-tabs" role="tablist" aria-label="Filter customers by status">
                {STATUS_FILTERS.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    role="tab"
                    aria-selected={selectedStatus === filter.key}
                    className={`segmented-button${selectedStatus === filter.key ? ' active' : ''}`}
                    onClick={() => setSelectedStatus(filter.key)}>
                    <span>{filter.label}</span>
                    <span className="client-status-count">{statusCounts[filter.key]}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {hasVisibleCustomers ? (
            <div className="panel">
              <div className="panel-title-row">
                <div>
                  <h2 className="panel-title">Customers</h2>
                  <p className="panel-caption">{usingApiClients ? 'From clients API' : 'From local visits'}</p>
                </div>
              </div>

              <div className="list">
                {usingApiClients ? (
                  filteredApiClients.map((client) => {
                    const clientKey = normalizeCustomerKey(client.key ?? client.number ?? client.id);

                    return (
                      <button
                        key={client.id}
                        type="button"
                        className="list-item interactive"
                        onClick={() => navigate(`/clients/${clientKey}`)}>
                        <div className="media-row start media-copy">
                          <div className="avatar">{getInitials(client.name)}</div>
                          <div>
                            <strong>{client.name}</strong>
                            <span>{client.number}</span>
                            <p>
                              {client.total_visits ?? client.appointment_count ?? 0} visits
                              {typeof client.total_spent === 'number'
                                ? ` · ${formatInrCurrency(client.total_spent)}`
                                : ''}
                            </p>
                            {client.latest_services?.length ? <p>{client.latest_services.join(' · ')}</p> : null}
                            {client.latest_staff_name ? <p>Last handled by {client.latest_staff_name}</p> : null}
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  visibleCustomers.map((customer) => (
                    <CustomerCard
                      key={customer.key}
                      customer={customer}
                      onPress={() => navigate(`/clients/${customer.key}`)}
                    />
                  ))
                )}
              </div>
            </div>
          ) : null}
        </div>

        {followUps.length > 0 || recentReviews.length > 0 ? (
          <div className="stack">
            {followUps.length > 0 ? (
              <div className="panel">
                <div className="panel-title-row">
                  <div>
                    <h2 className="panel-title">Follow-ups</h2>
                    <p className="panel-caption">Active threads</p>
                  </div>
                </div>

                <div className="list">
                  {followUps.map((customer) => (
                    <button
                      key={customer.key}
                      type="button"
                      className="list-item interactive"
                      onClick={() => navigate(`/clients/${customer.key}`)}>
                      <div>
                        <strong>{customer.name}</strong>
                        <span>{customer.phone}</span>
                        <p>{customer.latestServices.join(' · ')}</p>
                      </div>
                      <strong>{customer.statusLabel}</strong>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {recentReviews.length > 0 ? (
              <div className="panel">
                <div className="panel-title-row">
                  <div>
                    <h2 className="panel-title">Reviews</h2>
                    <p className="panel-caption">Latest feedback</p>
                  </div>
                </div>

                <div className="list">
                  {recentReviews.map((review) => (
                    <div className="list-item" key={review.id}>
                      <div>
                        <strong>{review.rating ? `${review.rating}/5` : 'Response'}</strong>
                        <span>{review.staffName}</span>
                        <p>{review.note ?? 'No note added.'}</p>
                      </div>
                      <strong>{review.serviceSummary}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
